import api from '../api';

export interface ProcessStep {
  id: number;
  project_id?: number;
  process_map_id?: number;
  process_id?: string;
  step_number: number;
  process_step_id?: string;
  process_step_name: string;
  
  // Location metadata
  performing_country?: string[] | string;
  performing_legal_entity?: string[] | string;
  affected_country?: string[] | string;
  affected_legal_entity?: string[] | string;
  
  // Process characteristics
  process_step_type?: 'Automated' | 'Semi-automated' | 'Manual' | '';
  supporting_models?: string[];
  non_model_tools?: string;
  
  // Third party information
  third_party_support_type?: 'All' | 'Partial' | 'None' | '';
  third_party_relationship_type?: string;
  external_parties_listing?: string;
  
  // Internal parties
  performer_relier_relationship?: boolean;
  relier?: string;
  relier_legal_entity?: string;
  performer_relier_details?: string;
  performer_relier_rationale?: string;
  
  // Additional metadata
  supporting_applications?: string[];
  description?: string;
  policy?: string;
  procedure?: string;
  swimlane_info?: any;
  
  // AI and validation
  ai_extracted?: boolean;
  validation_status?: string;
  validated_by?: string;
  validation_date?: string;
  
  created_at: string;
  updated_at: string;
}

export interface ProcessStepRisk {
  id: number;
  process_step_id: number;
  risk_category_id?: number;
  risk_id?: string;
  risk_name?: string;
  risk_description?: string;
  risk_taxonomy_path?: string;
  
  // Risk assessment
  inherent_likelihood?: number;
  inherent_impact?: number;
  inherent_risk_score?: number;
  residual_likelihood?: number;
  residual_impact?: number;
  residual_risk_score?: number;
  
  // AI identification
  ai_identified?: boolean;
  ai_confidence_score?: number;
  ai_rationale?: string;
  manual_override?: boolean;
  
  status?: string;
}

export interface ProcessStepControl {
  id: number;
  process_step_id: number;
  control_type_id?: number;
  control_id?: string;
  control_name?: string;
  control_description?: string;
  
  // Control characteristics
  control_type?: string;
  control_frequency?: string;
  control_nature?: string;
  automation_level?: string;
  
  // Effectiveness
  control_effectiveness?: 'Effective' | 'Needs Improvement' | 'Ineffective' | '';
  effectiveness_rating?: 'Effective' | 'Needs Improvement' | 'Ineffective';
  effectiveness_score?: number;
  
  // Regulatory
  regulatory_citations?: any[];
  compliance_requirements?: string;
  
  // Testing
  last_tested_date?: string;
  next_test_date?: string;
  test_results?: any;
  
  // AI recommendation
  ai_recommended?: boolean;
  ai_confidence_score?: number;
  ai_rationale?: string;
  
  // Ownership
  control_owner?: string;
  control_reviewer?: string;
  
  // Status
  status?: string;
  implementation_status?: string;
}

class ProcessStepServiceClass {
  private readonly basePath = '/api/process-steps';

  async getAll(): Promise<ProcessStep[]> {
    try {
      const response = await api.get(this.basePath);
      return response.data.data;
    } catch (error) {
      console.error('Failed to get process steps:', error);
      throw error;
    }
  }

  async getById(id: number): Promise<ProcessStep> {
    try {
      const response = await api.get(`${this.basePath}/${id}`);
      return response.data.data;
    } catch (error) {
      console.error(`Failed to get process step ${id}:`, error);
      throw error;
    }
  }

  async create(stepData: Partial<ProcessStep>): Promise<ProcessStep> {
    try {
      const response = await api.post(this.basePath, stepData);
      return response.data.data;
    } catch (error) {
      console.error('Failed to create process step:', error);
      throw error;
    }
  }

  async update(id: number, stepData: Partial<ProcessStep>): Promise<ProcessStep> {
    try {
      const response = await api.put(`${this.basePath}/${id}`, stepData);
      return response.data.data;
    } catch (error) {
      console.error(`Failed to update process step ${id}:`, error);
      throw error;
    }
  }

  async delete(id: number): Promise<void> {
    try {
      await api.delete(`${this.basePath}/${id}`);
    } catch (error) {
      console.error(`Failed to delete process step ${id}:`, error);
      throw error;
    }
  }

  async getRisks(stepId: number): Promise<ProcessStepRisk[]> {
    try {
      const response = await api.get(`/api/process-step-risks/process-step/${stepId}`);
      return response.data.data;
    } catch (error) {
      console.error(`Failed to get risks for process step ${stepId}:`, error);
      throw error;
    }
  }

  async addRisk(stepId: number, riskData: Partial<ProcessStepRisk>): Promise<ProcessStepRisk> {
    try {
      const response = await api.post(`/api/process-step-risks`, {
        ...riskData,
        process_step_id: stepId
      });
      return response.data.data;
    } catch (error) {
      console.error(`Failed to add risk to process step ${stepId}:`, error);
      throw error;
    }
  }

  async updateRisk(stepId: number, riskId: number, riskData: Partial<ProcessStepRisk>): Promise<ProcessStepRisk> {
    try {
      const response = await api.put(`/api/process-step-risks/${riskId}`, riskData);
      return response.data.data;
    } catch (error) {
      console.error(`Failed to update risk ${riskId} for process step ${stepId}:`, error);
      throw error;
    }
  }

  async deleteRisk(stepId: number, riskId: number): Promise<void> {
    try {
      await api.delete(`/api/process-step-risks/${riskId}`);
    } catch (error) {
      console.error(`Failed to delete risk ${riskId} from process step ${stepId}:`, error);
      throw error;
    }
  }

  async getControls(stepId: number): Promise<ProcessStepControl[]> {
    try {
      const response = await api.get(`/api/process-step-controls/process-step/${stepId}`);
      return response.data.data;
    } catch (error) {
      console.error(`Failed to get controls for process step ${stepId}:`, error);
      throw error;
    }
  }

  async addControl(stepId: number, controlData: Partial<ProcessStepControl>): Promise<ProcessStepControl> {
    try {
      const response = await api.post(`/api/process-step-controls`, {
        ...controlData,
        process_step_id: stepId
      });
      return response.data.data;
    } catch (error) {
      console.error(`Failed to add control to process step ${stepId}:`, error);
      throw error;
    }
  }

  async updateControl(stepId: number, controlId: number, controlData: Partial<ProcessStepControl>): Promise<ProcessStepControl> {
    try {
      const response = await api.put(`/api/process-step-controls/${controlId}`, controlData);
      return response.data.data;
    } catch (error) {
      console.error(`Failed to update control ${controlId} for process step ${stepId}:`, error);
      throw error;
    }
  }

  async deleteControl(stepId: number, controlId: number): Promise<void> {
    try {
      await api.delete(`/api/process-step-controls/${controlId}`);
    } catch (error) {
      console.error(`Failed to delete control ${controlId} from process step ${stepId}:`, error);
      throw error;
    }
  }

  async generateRCSA(processStepIds: number[]): Promise<any> {
    try {
      const response = await api.post(`${this.basePath}/generate-rcsa`, { processStepIds });
      return response.data.data;
    } catch (error) {
      console.error('Failed to generate RCSA:', error);
      throw error;
    }
  }

  async bulkImport(stepsData: Partial<ProcessStep>[]): Promise<ProcessStep[]> {
    try {
      const response = await api.post(`${this.basePath}/bulk-import`, { steps: stepsData });
      return response.data.data;
    } catch (error) {
      console.error('Failed to bulk import process steps:', error);
      throw error;
    }
  }

  // AI Recommendation Methods
  async suggestRisks(stepId: number, options?: { verify?: boolean }): Promise<{ data: ProcessStepRisk[]; verification_metadata: any }> {
    try {
      const response = await api.post(`${this.basePath}/${stepId}/ai/suggest-risks`, {
        verify: options?.verify === true,
        verification_mode: options?.verify === true ? 'standard' : 'off'
      });
      return { data: response.data.data, verification_metadata: response.data.verification_metadata || null };
    } catch (error) {
      console.error(`Failed to get AI risk suggestions for step ${stepId}:`, error);
      throw error;
    }
  }

  async suggestControls(stepId: number, options?: { verify?: boolean }): Promise<{ data: ProcessStepControl[]; verification_metadata: any }> {
    try {
      const response = await api.post(`${this.basePath}/${stepId}/ai/suggest-controls`, {
        verify: options?.verify === true,
        verification_mode: options?.verify === true ? 'standard' : 'off'
      });
      return { data: response.data.data, verification_metadata: response.data.verification_metadata || null };
    } catch (error) {
      console.error(`Failed to get AI control suggestions for step ${stepId}:`, error);
      throw error;
    }
  }

  async analyzeControlGaps(stepId: number, options?: { verify?: boolean }): Promise<any> {
    try {
      const response = await api.post(`${this.basePath}/${stepId}/ai/analyze-gaps`, {
        verify: options?.verify === true,
        verification_mode: options?.verify === true ? 'standard' : 'off'
      });
      return response.data.data;
    } catch (error) {
      console.error(`Failed to get AI gap analysis for step ${stepId}:`, error);
      throw error;
    }
  }
}

export const ProcessStepService = new ProcessStepServiceClass();
export default ProcessStepService;